// ===== useSonnetChat.js =====
// Multi-Agent AgentCore WebSocket Hook with Cognito Auth
// Renamed from SonnetChat.js to follow React hook conventions

import { useState, useEffect, useRef, useCallback } from 'react';
import { fetchAuthSession } from '@aws-amplify/auth';
import { linkify, hasHtmlLinks, stripHtml } from '../../utils/chatUtils';

const useSonnetChat = ({
  message,
  setMessage,
  setIsLoading,
  setError,
  setProcessingStage,
  isActive,
  userName,
  sessionId,
  onVersionResponse
}) => {
  // ──────────────────────────────────────────────
  // State
  // ──────────────────────────────────────────────
  const [sonnetMessages, setSonnetMessages] = useState([]);
  const [sonnetTypingText, setSonnetTypingText] = useState('');
  const [sonnetFullResponse, setSonnetFullResponse] = useState('');
  const [sonnetIsTyping, setSonnetIsTyping] = useState(false);
  const [sonnetWsConnected, setSonnetWsConnected] = useState(false);
  const [sonnetWsConnecting, setSonnetWsConnecting] = useState(false);
  const [currentStatus, setCurrentStatus] = useState('');
  const [agentStatus, setAgentStatus] = useState(null);
  const [pingCount, setPingCount] = useState(0);

  const [internalSessionId, setInternalSessionId] = useState(() => {
    return sessionId || `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  });

  // ──────────────────────────────────────────────
  // Refs — used to avoid stale closures in WS handlers
  // ──────────────────────────────────────────────
  const sonnetWsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const messageTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const updateThrottleRef = useRef(null);
  const pendingRef = useRef([]);
  const urlsRequestedRef = useRef(new Set());
  const streamingContentRef = useRef('');
  const urlRefreshCallbacksRef = useRef(new Map());
  const isConnectingRef = useRef(false);

  // Stable refs for values accessed inside WebSocket handlers
  // This prevents stale closures — the refs always hold the latest value
  const isActiveRef = useRef(isActive);
  const internalSessionIdRef = useRef(internalSessionId);
  const userNameRef = useRef(userName);
  const onVersionResponseRef = useRef(onVersionResponse);
  const setErrorRef = useRef(setError);
  const setIsLoadingRef = useRef(setIsLoading);
  const setProcessingStageRef = useRef(setProcessingStage);
  const modelChoiceRef = useRef('sonnet');


  // Keep refs in sync with latest values
  useEffect(() => { isActiveRef.current = isActive; }, [isActive]);
  useEffect(() => { internalSessionIdRef.current = internalSessionId; }, [internalSessionId]);
  useEffect(() => { userNameRef.current = userName; }, [userName]);
  useEffect(() => { onVersionResponseRef.current = onVersionResponse; }, [onVersionResponse]);
  useEffect(() => { setErrorRef.current = setError; }, [setError]);
  useEffect(() => { setIsLoadingRef.current = setIsLoading; }, [setIsLoading]);
  useEffect(() => { setProcessingStageRef.current = setProcessingStage; }, [setProcessingStage]);

  // ──────────────────────────────────────────────
  // Constants
  // ──────────────────────────────────────────────
  const typingSpeed = 10;
  
  //prod URL
  //const SONNET_WS_URL = process.env.REACT_APP_WS_URL || 'wss://db1qgvzw00.execute-api.us-east-2.amazonaws.com/production/';
  
  //dev URL
  //const SONNET_WS_URL = process.env.REACT_APP_WS_URL || 'wss://db1qgvzw00.execute-api.us-east-2.amazonaws.com/dev/';
  const SONNET_WS_URL = process.env.REACT_APP_WS_URL || 'wss://9ujwrgtwpb.execute-api.us-east-2.amazonaws.com/dev/';

  
  const MESSAGE_TIMEOUT = 180_000;
  const PING_INTERVAL = 30_000;
  const STREAM_THROTTLE_MS = 30;
  const TOKEN_REFRESH_INTERVAL = 45 * 60 * 1000; // 45 minutes

  // ──────────────────────────────────────────────
  // URL Refresh Helpers
  // ──────────────────────────────────────────────
  const registerUrlRefreshCallback = useCallback((s3Key, callback) => {
    const key = s3Key && s3Key.trim();
    if (!key) {
      console.warn('Attempted to register callback with empty s3Key');
      return;
    }
    urlRefreshCallbacksRef.current.set(key, callback);
  }, []);

  const requestUrlRefresh = useCallback((s3Key) => {
    if (!sonnetWsRef.current || sonnetWsRef.current.readyState !== WebSocket.OPEN) {
      console.error('Cannot request URL refresh — WebSocket not connected');
      return;
    }

    const currentUserName = userNameRef.current;
    const currentSessionId = internalSessionIdRef.current;

    if (!currentUserName || !currentSessionId) {
      console.error('Cannot request URL — userName or sessionId missing');
      return;
    }

    const refreshRequest = {
      action: 'refresh-url',
      s3Key,
      userName: currentUserName,
      sessionId: currentSessionId
    };

    try {
      sonnetWsRef.current.send(JSON.stringify(refreshRequest));
    } catch (error) {
      console.error('Error sending URL refresh request:', error);
    }
  }, []);

  // ──────────────────────────────────────────────
  // Cleanup — tears down WebSocket and all timers
  // NOTE: Does NOT reset isConnectingRef — only connect() manages that
  // ──────────────────────────────────────────────
  const cleanup = useCallback(() => {
    if (sonnetWsRef.current) {
      const ws = sonnetWsRef.current;
      ws.onclose = null;
      ws.onerror = null;
      ws.onmessage = null;
      ws.onopen = null;
      if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
        ws.close(1000, 'Cleanup');
      }
      sonnetWsRef.current = null;
    }
    clearTimeout(reconnectTimeoutRef.current);
    clearTimeout(messageTimeoutRef.current);
    clearInterval(pingIntervalRef.current);
    clearTimeout(updateThrottleRef.current);
    setSonnetWsConnected(false);
    setSonnetWsConnecting(false);
    setCurrentStatus('');
    setAgentStatus(null);
    setPingCount(0);
    streamingContentRef.current = '';
    urlRefreshCallbacksRef.current.clear();
    urlsRequestedRef.current.clear();
  }, []);

  // Expose a setter
  const setModelChoice = useCallback((model) => {
      modelChoiceRef.current = model;
  }, []);

  // ──────────────────────────────────────────────
  // Stop all loading / processing indicators
  // Uses refs so it's safe inside WS handlers
  // ──────────────────────────────────────────────
  const stopAllLoading = useCallback(() => {
    setIsLoadingRef.current(false);
    setProcessingStageRef.current(null);
    setCurrentStatus('');
    setAgentStatus(null);
    clearTimeout(messageTimeoutRef.current);
    clearTimeout(updateThrottleRef.current);
  }, []);

  //Delete report
  const deleteReport = useCallback((reportSessionId) => {
    if (!sonnetWsRef.current || sonnetWsRef.current.readyState !== WebSocket.OPEN) return;
    sonnetWsRef.current.send(JSON.stringify({
      action: 'deleteReport',
      userName: userNameRef.current,
      reportSessionId,
    }));
  }, []);

  // ──────────────────────────────────────────────
  // Auth — retrieve Cognito ID token
  // ──────────────────────────────────────────────
  const getAuthToken = useCallback(async () => {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      if (token) return token;
      console.warn('⚠️ No ID token found in Cognito session');
      return null;
    } catch (error) {
      console.error('❌ Failed to get auth token:', error);
      return null;
    }
  }, []);

  // ──────────────────────────────────────────────
  // Session / History helpers
  // ──────────────────────────────────────────────
  const requestHistory = useCallback((sid) => {
    if (!sonnetWsRef.current || sonnetWsRef.current.readyState !== WebSocket.OPEN) return;
    sonnetWsRef.current.send(JSON.stringify({ action: 'getHistory', sessionId: sid }));
  }, []);

  const requestSessionsList = useCallback(() => {
      if (!sonnetWsRef.current || sonnetWsRef.current.readyState !== WebSocket.OPEN) {
          console.warn('⚠️ requestSessionsList: WebSocket not open');
          return;
      }
      const currentUserName = userNameRef.current;
      if (!currentUserName) {
          console.warn('⚠️ requestSessionsList: userName not available yet');
          return;
      }
      console.log('📋 Requesting sessions list for:', currentUserName);
      sonnetWsRef.current.send(JSON.stringify({
          action: 'listSessions',
          userName: currentUserName,
      }));
  }, []);

  const requestNewSession = useCallback(() => {
    if (!sonnetWsRef.current || sonnetWsRef.current.readyState !== WebSocket.OPEN) return;
    sonnetWsRef.current.send(JSON.stringify({ action: 'newSession' }));
  }, []);

  const clearHistory = useCallback((sid) => {
    if (!sonnetWsRef.current || sonnetWsRef.current.readyState !== WebSocket.OPEN) return;
    sonnetWsRef.current.send(JSON.stringify({
      action: 'clearHistory',
      sessionId: sid || internalSessionIdRef.current
    }));
  }, []);

  const deleteSession = useCallback((sid) => {
    if (!sonnetWsRef.current || sonnetWsRef.current.readyState !== WebSocket.OPEN) return;
    sonnetWsRef.current.send(JSON.stringify({ action: 'deleteSession', sessionId: sid }));
  }, []);

  const requestReportsList = useCallback(() => {
      if (!sonnetWsRef.current || sonnetWsRef.current.readyState !== WebSocket.OPEN) {
          console.warn('⚠️ requestReportsList: WebSocket not open');
          return;
      }
      const currentUserName = userNameRef.current;
      if (!currentUserName) {
          console.warn('⚠️ requestReportsList: userName not available yet');
          return;
      }
      console.log('📄 Requesting reports list for:', currentUserName);
      sonnetWsRef.current.send(JSON.stringify({
          action: 'listReports',
          userName: currentUserName,
      }));
  }, []);

  const requestReportContent = useCallback((reportSessionId) => {
      if (!sonnetWsRef.current || sonnetWsRef.current.readyState !== WebSocket.OPEN) return;
      sonnetWsRef.current.send(JSON.stringify({
          action: 'getReport',
          userName: userNameRef.current,
          reportSessionId,
      }));
  }, []);

  // ──────────────────────────────────────────────
  // sendRaw — low-level WebSocket send
  // Uses refs for session/user so it never goes stale
  // ──────────────────────────────────────────────
  const sendRaw = useCallback((ws, txt) => {
    if (!ws || ws.readyState !== WebSocket.OPEN) return false;

    let messagePayload;
    try {
      messagePayload = JSON.parse(txt);
    } catch {
        messagePayload = {
          action: 'sendMessage',
          message: txt,
          sessionId: internalSessionIdRef.current,
          model: modelChoiceRef.current,  // ← Dynamic now
          userName: userNameRef.current || 'default_user',
      };
    }

    try {
      ws.send(JSON.stringify(messagePayload));

      if (messagePayload.action === 'sendMessage') {
        clearTimeout(messageTimeoutRef.current);
        messageTimeoutRef.current = setTimeout(() => {
          stopAllLoading();
          setErrorRef.current('Agent is taking too long to respond.');
          setSonnetMessages(prev => prev.filter(m => !m.isLoading && !m.isStreaming));
        }, MESSAGE_TIMEOUT);
      }
      return true;
    } catch (error) {
      console.error('Failed to send message:', error);
      setErrorRef.current('Failed to send message to agent');
      return false;
    }
  }, [stopAllLoading, MESSAGE_TIMEOUT]);

  // ──────────────────────────────────────────────
  // handleMessage — processes every inbound WS frame
  // Extracted so connect() doesn't capture stale state
  // All mutable values read from refs
  // ──────────────────────────────────────────────
  const handleMessage = useCallback((evt) => {
    let payload;
    try {
      payload = JSON.parse(evt.data);
    } catch {
      console.error('Invalid JSON from AgentCore:', evt.data);
      return;
    }

    switch (payload.type) {

      // ── Session Management ──
      case 'session_created':
      case 'new_session': {
        if (payload.sessionId) {
          setInternalSessionId(payload.sessionId);
        }
        if (payload.type === 'new_session') {
          setSonnetMessages([]);
        }
        break;
      }

      case 'history': {
          // ALWAYS set messages, even if empty — clears previous session's messages
          const historyMessages = (payload.messages || []).map((msg, index) => ({
              text: msg.content || '',
              isUser: msg.role === 'user',
              isTyping: false,
              isLoading: false,
              id: `history-${payload.sessionId || 'unknown'}-${index}-${Date.now()}`
          }));
          setSonnetMessages(historyMessages);

          if (payload.sessionId) {
              setInternalSessionId(payload.sessionId);
          }

          // Reset streaming state in case we were mid-stream
          streamingContentRef.current = '';
          stopAllLoading();
          break;
      }

      case 'sessions_list': {
        if (onVersionResponseRef.current) {
          onVersionResponseRef.current({
            type: 'sessions-list',
            sessions: payload.sessions || [],
            filterModel: payload.filterModel
          });
        }
        break;
      }

      case 'session_deleted': {
        if (onVersionResponseRef.current) {
          onVersionResponseRef.current({
            type: 'session-deleted',
            sessionId: payload.sessionId
          });
        }
        break;
      }

      case 'report_deleted': {
          if (onVersionResponseRef.current) {
              onVersionResponseRef.current({
                  type: 'report-deleted',
                  sessionId: payload.sessionId,
                  success: payload.success,
                  deletedCount: payload.deletedCount,
                  error: payload.error,
              });
          }
          break;
      }

      case 'history_cleared': {
        if (payload.sessionId === internalSessionIdRef.current) {
          setSonnetMessages([]);
        }
        break;
      }

      case 'summary_generated': {
        if (onVersionResponseRef.current) {
          onVersionResponseRef.current({
            type: 'summary-generated',
            sessionId: payload.sessionId,
            summary: payload.summary
          });
        }
        break;
      }

      // ── Streaming Response ──
      case 'stream_start': {
        clearTimeout(messageTimeoutRef.current);
        streamingContentRef.current = '';

        setCurrentStatus('Agent is thinking...');
        setAgentStatus(payload.agentStatus || 'thinking');

        setSonnetMessages(prev => {
          const filtered = prev.filter(m => !m.isLoading && !m.isStreaming);
          return [...filtered, {
            text: '',
            isUser: false,
            isStreaming: true,
            isTyping: false,
            id: `ai-stream-${Date.now()}`
          }];
        });
        break;
      }

      case 'content_block_delta': {
        const text = payload.delta?.text || '';
        if (text) {
          streamingContentRef.current += text;

          if (!updateThrottleRef.current) {
            updateThrottleRef.current = setTimeout(() => {
              const contentSnapshot = streamingContentRef.current;
              const formatted = hasHtmlLinks(contentSnapshot)
                ? contentSnapshot
                : linkify(contentSnapshot);

              setSonnetMessages(prev => {
                const newMessages = [...prev];
                const lastIndex = newMessages.length - 1;
                if (lastIndex >= 0 && newMessages[lastIndex].isStreaming) {
                  newMessages[lastIndex] = {
                    ...newMessages[lastIndex],
                    text: formatted
                  };
                }
                return newMessages;
              });

              updateThrottleRef.current = null;
            }, STREAM_THROTTLE_MS);
          }
        }
        break;
      }

      case 'stream_end': {
        if (updateThrottleRef.current) {
          clearTimeout(updateThrottleRef.current);
          updateThrottleRef.current = null;
        }

        const finalContent = streamingContentRef.current;
        const formatted = hasHtmlLinks(finalContent)
          ? finalContent
          : linkify(finalContent);

        setSonnetMessages(prev => {
          const newMessages = [...prev];
          const lastIndex = newMessages.length - 1;
          if (lastIndex >= 0 && newMessages[lastIndex].isStreaming) {
            newMessages[lastIndex] = {
              ...newMessages[lastIndex],
              text: formatted,
              isStreaming: false,
              isTyping: false
            };
          }
          return newMessages;
        });

        streamingContentRef.current = '';
        stopAllLoading();
        break;
      }

      // ── Agent Status Updates ──
      case 'agent_status': {
        const statusMessages = {
          'orchestrator_thinking': 'Analyzing your request...',
          'thinking': payload.detail
            ? `Thinking: ${payload.detail.substring(0, 100)}...`
            : 'Thinking...',
          'tool_executing': `${payload.agent || payload.tool || 'Sub-agent'} is working...`,
          'generating_response': 'Writing response...',
        };

        const statusText = statusMessages[payload.status] || payload.status;
        setCurrentStatus(statusText);
        setAgentStatus(payload.status);
        setProcessingStageRef.current(payload.agent || payload.status);

        clearTimeout(messageTimeoutRef.current);
        messageTimeoutRef.current = setTimeout(() => {
          stopAllLoading();
          setErrorRef.current('Agent is taking too long to respond.');
          setSonnetMessages(prev => prev.filter(m => !m.isLoading && !m.isStreaming));
        }, MESSAGE_TIMEOUT);
        break;
      }

      // ── Errors ──
      case 'error': {
        console.error('❌ Agent error:', payload.message);
        stopAllLoading();
        setErrorRef.current(payload.message || 'Agent error');
        setSonnetMessages(prev => prev.filter(m => !m.isLoading && !m.isStreaming));
        break;
      }
      case 'reports_list': {
          if (onVersionResponseRef.current) {
              onVersionResponseRef.current({
                  type: 'reports-list',
                  reports: payload.reports || [],
                  count: payload.count || 0,
              });
          }
          break;
      }

      case 'report_content': {
          if (onVersionResponseRef.current) {
              onVersionResponseRef.current({
                  type: 'report-content',
                  sessionId: payload.sessionId,
                  content: payload.content || '',
                  found: payload.found,
                  error: payload.error,
              });
          }
          break;
      }

      // ── Legacy / Other handlers ──
      default: {
        if (payload.status === 'pong') {
          setPingCount(prev => prev + 1);
          return;
        }

        if (payload.action === 'url-refreshed') {
          const { s3Key: rawS3Key, url, success, error: refreshError, expiresAt } = payload;
          const s3Key = rawS3Key && rawS3Key.trim();
          const callback = urlRefreshCallbacksRef.current.get(s3Key);
          if (callback) {
            try {
              callback(
                success && url
                  ? { success: true, url, expiresAt }
                  : { success: false, error: refreshError || 'Failed to refresh URL' }
              );
            } catch (cbErr) {
              console.error('Error in URL refresh callback:', cbErr);
            } finally {
              urlRefreshCallbacksRef.current.delete(s3Key);
            }
          }
          return;
        }

        if (payload.action === 'version-created') {
          if (onVersionResponseRef.current) {
            onVersionResponseRef.current({
              type: 'version-created',
              version: payload.version,
              description: payload.description,
              timestamp: payload.timestamp
            });
          }
          return;
        }

        if (payload.status === 'complete' || payload.completion) {
          const text = payload.completion || '';
          stopAllLoading();

          if (text && text.trim()) {
            const legacyFormatted = hasHtmlLinks(text) ? text : linkify(text);
            setSonnetFullResponse(legacyFormatted);
            setSonnetTypingText('');
            setSonnetMessages(prev => {
              const filtered = prev.filter(m => !m.isLoading && !m.isTyping);
              return [...filtered, {
                text: '',
                isUser: false,
                isTyping: true,
                id: `ai-typing-${Date.now()}`
              }];
            });
            setSonnetIsTyping(true);
          }
          return;
        }

        if (payload.status === 'error') {
          console.error('Legacy agent error:', payload.details || payload.error);
          stopAllLoading();
          setErrorRef.current(payload.details || payload.error || 'Agent error');
          setSonnetMessages(prev => prev.filter(m => !m.isLoading && !m.isTyping));
          return;
        }

        console.log('⚠️ Unhandled message type:', payload.type || 'unknown');
        break;
      }
    }
  }, [stopAllLoading, MESSAGE_TIMEOUT, STREAM_THROTTLE_MS]);

  // ──────────────────────────────────────────────
  // connect — async, creates authenticated WebSocket
  // Minimal dependency array — uses refs for everything mutable
  // ──────────────────────────────────────────────
  const connect = useCallback(async () => {
    if (!isActiveRef.current) return;
    if (isConnectingRef.current) return;

    if (
      sonnetWsRef.current &&
      (sonnetWsRef.current.readyState === WebSocket.OPEN ||
        sonnetWsRef.current.readyState === WebSocket.CONNECTING)
    ) {
      return;
    }

    isConnectingRef.current = true;
    cleanup();
    setSonnetWsConnecting(true);
    setErrorRef.current(null);

    try {
      const authToken = await getAuthToken();

      if (!authToken) {
        console.error('❌ No auth token — cannot connect WebSocket');
        setErrorRef.current('Authentication required. Please log in again.');
        setSonnetWsConnecting(false);
        isConnectingRef.current = false;
        return;
      }

      // Re-check after async gap
      if (!isActiveRef.current) {
        isConnectingRef.current = false;
        setSonnetWsConnecting(false);
        return;
      }

      const wsUrlWithAuth = `${SONNET_WS_URL}?token=${encodeURIComponent(authToken)}&userName=${encodeURIComponent(userNameRef.current || '')}`;

      console.log('🔌 Attempting authenticated WebSocket connection...');

      const ws = new WebSocket(wsUrlWithAuth);
      sonnetWsRef.current = ws;

      ws.onopen = () => {
        console.log('🟢 AgentCore WS connected');
        setSonnetWsConnected(true);
        setSonnetWsConnecting(false);
        isConnectingRef.current = false;

        // Keep-alive ping
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            try {
              ws.send(JSON.stringify({ action: 'ping' }));
            } catch (e) {
              console.warn('Ping failed:', e);
            }
          }
        }, PING_INTERVAL);

        // Request history for current session
        const currentSid = internalSessionIdRef.current;
        if (currentSid) {
          requestHistory(currentSid);
        }

        // Flush pending messages
        const pending = [...pendingRef.current];
        pendingRef.current = [];
        pending.forEach(txt => {
          if (!sendRaw(ws, txt)) {
            pendingRef.current.push(txt);
          }
        });
      };

      ws.onmessage = handleMessage;

      ws.onclose = (evt) => {
        console.warn('AgentCore WS closed:', evt.code, evt.reason);
        setSonnetWsConnected(false);
        setSonnetWsConnecting(false);
        isConnectingRef.current = false;
        clearInterval(pingIntervalRef.current);
        clearTimeout(updateThrottleRef.current);

        // Auto-reconnect on unexpected close
        if (isActiveRef.current && evt.code !== 1000) {
          console.log('🔄 Will attempt reconnect in 3s...');
          reconnectTimeoutRef.current = setTimeout(() => {
            if (isActiveRef.current) {
              connect();
            }
          }, 3000);
        }
      };

      ws.onerror = (evt) => {
        console.error('AgentCore WS error:', evt);
        // isConnectingRef cleared by onclose which always fires after onerror
      };
    } catch (err) {
      console.error('Failed to create WebSocket:', err);
      setErrorRef.current('Failed to establish connection.');
      setSonnetWsConnecting(false);
      isConnectingRef.current = false;
    }
  }, [SONNET_WS_URL, PING_INTERVAL, cleanup, getAuthToken, handleMessage, requestHistory, sendRaw]);

  // ──────────────────────────────────────────────
  // sendMessageToSonnet — public send API
  // ──────────────────────────────────────────────
  const sendMessageToSonnet = useCallback((messageToSend) => {
    const txt = (messageToSend || message).trim();
    if (!txt) return;

    setCurrentStatus('');
    setAgentStatus(null);
    setPingCount(0);
    streamingContentRef.current = '';

    // Detect action-only payloads (don't show in UI)
    let isActionOnly = false;
    try {
      const parsed = JSON.parse(txt);
      if (parsed.action && parsed.action !== 'sendMessage') {
        isActionOnly = true;
      }
    } catch {
      // Regular text message
    }

    if (
      sonnetWsRef.current &&
      sonnetWsRef.current.readyState === WebSocket.OPEN
    ) {
      if (!sendRaw(sonnetWsRef.current, txt)) {
        pendingRef.current.push(txt);
      }
    } else {
      pendingRef.current.push(txt);
      if (!isConnectingRef.current) {
        connect();
      }
    }

    if (isActionOnly) return;

    setIsLoadingRef.current(true);
    setErrorRef.current(null);
    setSonnetMessages(prev => [
      ...prev,
      { text: txt, isUser: true, id: `user-${Date.now()}` }
    ]);
    if (!messageToSend) setMessage('');
  }, [message, sendRaw, connect, setMessage]);

  // ──────────────────────────────────────────────
  // Typewriter effect for legacy (non-streaming) responses
  // ──────────────────────────────────────────────
  useEffect(() => {
    if (!sonnetIsTyping || !sonnetFullResponse) return;

    const fullText = sonnetFullResponse;
    const strippedFullText = stripHtml(fullText);
    const strippedCurrentText = stripHtml(sonnetTypingText);

    if (strippedCurrentText.length < strippedFullText.length) {
      const timeout = setTimeout(() => {
        const nextLength = Math.min(strippedCurrentText.length + 2, strippedFullText.length);
        let actualPosition = 0;
        let strippedPosition = 0;

        while (strippedPosition < nextLength && actualPosition < fullText.length) {
          if (fullText[actualPosition] === '<') {
            while (actualPosition < fullText.length && fullText[actualPosition] !== '>') {
              actualPosition++;
            }
            actualPosition++;
          } else {
            strippedPosition++;
            actualPosition++;
          }
        }

        setSonnetTypingText(fullText.slice(0, actualPosition));
      }, typingSpeed);

      return () => clearTimeout(timeout);
    } else {
      setSonnetIsTyping(false);

      setSonnetMessages(prev =>
        prev.map((m, i) =>
          i === prev.length - 1 && m.isTyping
            ? { ...m, text: fullText, isTyping: false }
            : m
        )
      );

      setSonnetTypingText('');
      setSonnetFullResponse('');
    }
  }, [sonnetIsTyping, sonnetFullResponse, sonnetTypingText, typingSpeed]);

  // ──────────────────────────────────────────────
  // Proactive token refresh for long-lived sessions
  // Reconnects with a fresh Cognito token before expiry
  // ──────────────────────────────────────────────
  useEffect(() => {
    if (!sonnetWsConnected) return;

    const refreshInterval = setInterval(async () => {
      console.log('🔄 Proactively refreshing auth session...');
      try {
        await fetchAuthSession({ forceRefresh: true });
        // Reconnect with fresh token
        if (isActiveRef.current) {
          isConnectingRef.current = false; // Allow reconnect
          connect();
        }
      } catch (err) {
        console.error('Token refresh failed:', err);
      }
    }, TOKEN_REFRESH_INTERVAL);

    return () => clearInterval(refreshInterval);
  }, [sonnetWsConnected, connect, TOKEN_REFRESH_INTERVAL]);

  // ──────────────────────────────────────────────
  // Connection lifecycle — connect on active, cleanup on inactive
  // ──────────────────────────────────────────────
  useEffect(() => {
    if (isActive) {
      connect();
    } else {
      cleanup();
    }
    return () => {
      cleanup();
    };
  }, [isActive, connect, cleanup]);

  // ──────────────────────────────────────────────
  // Public API
  // ──────────────────────────────────────────────
  return {
    sonnetMessages,
    sonnetTypingText,
    sonnetIsTyping,
    sonnetWsConnected,
    sonnetWsConnecting,
    sonnetSendMessage: sendMessageToSonnet,
    sonnetSessionId: internalSessionId,
    currentStatus,
    agentStatus,
    pingCount,
    requestUrlRefresh,
    registerUrlRefreshCallback,
    requestHistory,
    requestSessionsList,
    requestNewSession,
    clearHistory,
    deleteSession,
    requestReportsList,      
    requestReportContent, 
    deleteReport, 
  };
};

export default useSonnetChat;